import { useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import {
  hideAdGroupPlacementEdit,
  hidePreview,
  setPlacementCategories,
  setPlacementCategory,
  setPlacementType,
  showPreview,
  updateAdGroupsPlacement,
} from 'features/dashboard/operation/adGroupPlacementEdit/adGroupPlacementEditSlice'
import { Modal } from 'components/dialog/Modals'
import { PreviewConfirmModal } from 'components/dialog/ModalTemplate'
import { isEmpty, isEmptyObject, isSetEqual } from 'utils'
import Table from 'components/table/Table'
import RadioButton from 'components/form/RadioButton'
import CheckBox from 'components/form/CheckBox'
import { fail, failWithErrorResponse, success } from 'features/toast/Toast'
import useValidator from 'utils/hooks/useValidator'
import classNames from 'classnames'
import PlacementName from 'features/dashboard/operation/adGroupPlacementEdit/PlacementName'
import Loading from 'components/loading/Loading'
import { refreshDashboard } from 'features/dashboard/dashboardCommonSlice'
import FixedTable from 'components/table/FixedTable'

export const placementEquals = (placement, other) => {
  if (placement.type === 'ALL' && other.type === 'ALL') {
    return true
  } else {
    return (
      placement.type === other.type &&
      isSetEqual(placement.categories, other.categories)
    )
  }
}

const AdGroupPlacementEditModal = () => {
  const dispatch = useDispatch()
  const {
    visible,
    isSubmitting,
    previewVisible,
    isLoading,
    adGroups,
    loadingError,
    form: { placementType, placementCategories },
    preview: {
      placementType: previewType,
      placementCategories: previewCategories,
    },
  } = useSelector(
    ({ dashboard }) => dashboard.operation.adGroupPlacementEdit,
    shallowEqual
  )
  const { errors, validate } = useValidator(
    () => ({
      placement:
        placementType === 'SETTING' &&
        isEmpty(placementCategories) &&
        '매체유형 상세설정에 선택된 값이 없습니다.',
    }),
    [placementType, placementCategories]
  )
  const handleCheckboxChange = ({ target: { value, checked } }) => {
    dispatch(
      setPlacementCategory({
        checked,
        value,
      })
    )
  }
  const handlePreview = async () => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        dispatch(showPreview())
      }
    } catch (ignore) {}
  }
  const handleConfirm = async ({ preventClose }) => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        const { error } = await dispatch(updateAdGroupsPlacement())
        if (error) {
          preventClose()
          fail(
            '광고그룹 매체유형 변경사항을 적용할 수 없습니다.\n다시 시도해 주세요.'
          )
        } else {
          dispatch(refreshDashboard())
          success('광고그룹 매체유형 변경사항이 적용되었습니다.')
        }
      }
    } catch (error) {
      preventClose()
    }
  }

  useEffect(() => {
    if (!isEmptyObject(errors)) {
      dispatch(hidePreview())
    }
  }, [dispatch, errors])
  useEffect(
    () => () => {
      dispatch(hideAdGroupPlacementEdit())
    },
    [dispatch]
  )
  useEffect(() => {
    if (loadingError) {
      dispatch(hideAdGroupPlacementEdit())
      failWithErrorResponse(loadingError, {
        messageMap: {
          104007: '삭제된 값이 존재합니다.\n새로고침 후 다시 시도해 주세요.',
        },
      })
    }
  }, [dispatch, loadingError])

  const preview = previewVisible && isEmptyObject(errors)
  const changed = !placementEquals(
    {
      type: placementType,
      categories: placementCategories,
    },
    {
      type: previewType,
      categories: previewCategories,
    }
  )

  return (
    <Modal on={visible} className="media_layer">
      <PreviewConfirmModal
        title="광고그룹 매체유형 변경"
        isSubmitting={isSubmitting}
        onPreview={handlePreview}
        onConfirm={handleConfirm}
        previewDisabled={!changed && preview}
        confirmDisabled={changed && preview}
        handleClose={() => dispatch(hideAdGroupPlacementEdit())}>
        <p className="desc_copy">
          광고가 노출되는 게재지면을 설정합니다. 검색 매체는 검색어와 광고를
          매칭하는 게재지면을 의미하며, 콘텐츠 매체는 문맥과 광고를 매칭하는
          게재지면을 의미합니다.
        </p>
        {isLoading ? (
          <div className="box_subcard">
            <Loading />
          </div>
        ) : (
          <>
            <ul className="list_radiocheck">
              <li>
                <RadioButton
                  name="placementType"
                  value="ALL"
                  label="전체"
                  checked={placementType === 'ALL'}
                  onValueChange={v => dispatch(setPlacementType(v))}
                />
              </li>
              <li>
                <RadioButton
                  name="placementType"
                  value="SETTING"
                  label="상세설정"
                  checked={placementType === 'SETTING'}
                  onValueChange={v => dispatch(setPlacementType(v))}
                />
              </li>
            </ul>
            {placementType === 'SETTING' && (
              <div
                className={classNames('box_subcard', {
                  error: !isEmptyObject(errors),
                })}>
                <strong className="screen_out">매체유형 상세 설정</strong>
                <ul className="list_radiocheck">
                  <li>
                    <CheckBox
                      label="검색 매체"
                      checked={
                        placementCategories.includes(
                          'PREMIUM_LINK_SEARCH_KAKAO'
                        ) ||
                        placementCategories.includes(
                          'PREMIUM_LINK_SEARCH_PARTNER'
                        )
                      }
                      onCheckedChange={checked =>
                        dispatch(
                          setPlacementCategories({
                            checked,
                            values: checked
                              ? ['PREMIUM_LINK_SEARCH_KAKAO']
                              : [
                                  'PREMIUM_LINK_SEARCH_KAKAO',
                                  'PREMIUM_LINK_SEARCH_PARTNER',
                                ],
                          })
                        )
                      }
                    />
                    <ul className="list_radiocheck">
                      <li>
                        <CheckBox
                          label="카카오, 다음"
                          name="placementCategories"
                          value="PREMIUM_LINK_SEARCH_KAKAO"
                          checked={placementCategories.includes(
                            'PREMIUM_LINK_SEARCH_KAKAO'
                          )}
                          onChange={handleCheckboxChange}
                        />
                      </li>
                    </ul>
                  </li>
                  <li>
                    <CheckBox
                      label="콘텐츠 매체"
                      checked={
                        placementCategories.includes(
                          'PREMIUM_LINK_CONTENT_KAKAO'
                        ) ||
                        placementCategories.includes(
                          'PREMIUM_LINK_CONTENT_PARTNER'
                        )
                      }
                      onCheckedChange={checked =>
                        dispatch(
                          setPlacementCategories({
                            checked,
                            values: [
                              'PREMIUM_LINK_CONTENT_KAKAO',
                              'PREMIUM_LINK_CONTENT_PARTNER',
                            ],
                          })
                        )
                      }
                    />
                    <ul className="list_radiocheck">
                      <li>
                        <CheckBox
                          label="카카오, 다음"
                          name="placementCategories"
                          value="PREMIUM_LINK_CONTENT_KAKAO"
                          checked={placementCategories.includes(
                            'PREMIUM_LINK_CONTENT_KAKAO'
                          )}
                          onChange={handleCheckboxChange}
                        />
                      </li>
                      <li>
                        <CheckBox
                          label="파트너"
                          name="placementCategories"
                          value="PREMIUM_LINK_CONTENT_PARTNER"
                          checked={placementCategories.includes(
                            'PREMIUM_LINK_CONTENT_PARTNER'
                          )}
                          onChange={handleCheckboxChange}
                        />
                      </li>
                    </ul>
                  </li>
                </ul>
              </div>
            )}
            {errors.placement && (
              <p className="txt_error">{errors.placement}</p>
            )}
            {preview && (
              <FixedTable caption="광고그룹 매체유형 변경 이력 표">
                <Table.Head>
                  <Table.Column
                    title="선택 광고그룹"
                    className="inner_cell"
                    width={194}
                  />
                  <Table.Column
                    title="변경 전"
                    className="inner_cell"
                    width={172}
                  />
                  <Table.Column title="변경 후" className="inner_cell" />
                </Table.Head>
                <Table.Body>
                  {adGroups.map(
                    ({ id, name, placement: { type, categories } }) => (
                      <Table.Row
                        rowId={id}
                        key={id}
                        className={classNames({
                          update: !placementEquals(
                            {
                              type,
                              categories,
                            },
                            {
                              type: previewType,
                              categories: previewCategories,
                            }
                          ),
                        })}>
                        <td>
                          <div className="inner_cell">
                            <span className="txt_limit2">{name}</span>
                          </div>
                        </td>
                        <td>
                          <div className="inner_cell">
                            <PlacementName
                              type={type}
                              categories={categories}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="inner_cell">
                            <PlacementName
                              type={previewType}
                              categories={previewCategories}
                            />
                          </div>
                        </td>
                      </Table.Row>
                    )
                  )}
                </Table.Body>
              </FixedTable>
            )}
          </>
        )}
      </PreviewConfirmModal>
    </Modal>
  )
}
export default AdGroupPlacementEditModal
